import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStockAuth } from '../../context/StockAuthContext';
import toast from 'react-hot-toast';

const B = '#0B3D6E', T = '#0F6E56';

function PasswordStrength({ password }) {
  const checks = [
    { label: '8 caractères minimum', ok: password.length >= 8 },
    { label: 'Une majuscule', ok: /[A-Z]/.test(password) },
    { label: 'Un chiffre', ok: /[0-9]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const colors = ['#dc2626','#d97706','#0F6E56'];
  const labels = ['Faible','Moyen','Fort'];
  if (!password) return null;
  return (
    <div style={{ marginTop:6 }}>
      <div style={{ display:'flex', gap:4, marginBottom:4 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i < score ? colors[score-1] : '#e2e8f0', transition:'background 0.3s' }}/>
        ))}
      </div>
      <div style={{ fontSize:10, color: score>0?colors[score-1]:'#94a3b8' }}>{score>0?labels[score-1]:''}</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
        {checks.map(c => (
          <span key={c.label} style={{ fontSize:10, color:c.ok?T:'#94a3b8', display:'flex', alignItems:'center', gap:3 }}>
            {c.ok?'✓':'○'} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function StockAuth() {
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { login, register } = useStockAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username:'', email:'', password:'', confirmPassword:'',
    firstName:'', lastName:'', phone:'', company:'', companyType:'pharmacie',
    agreeTerms: false
  });
  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const inp = {
    width:'100%', border:'1px solid #e2e8f0', borderRadius:8,
    padding:'10px 12px', fontSize:13, background:'#f8fafc',
    outline:'none', fontFamily:'inherit', boxSizing:'border-box',
    transition:'border-color 0.2s'
  };

  const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      if (!validateEmail(form.email)) { toast.error('Email invalide'); return; }
      if (form.password !== form.confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
      if (form.password.length < 6) { toast.error('Mot de passe trop court (min 6 caractères)'); return; }
      if (!form.agreeTerms) { toast.error('Veuillez accepter les conditions d\'utilisation'); return; }
      if (!form.company) { toast.error('Nom de l\'établissement requis'); return; }
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await login(form.username, form.password);
        if (res.user.role === 'client' || res.user.role === 'admin') {
          toast.success(`Bienvenue, ${res.user.firstName || res.user.username} !`);
          navigate('/stock/dashboard');
        }
      } else {
        await register(form);
        setEmailSent(true);
        toast.success('Compte créé ! Bienvenue sur StockMed.');
        navigate('/stock/dashboard');
      }
    } catch(err) {
      toast.error(err.response?.data?.message || 'Erreur de connexion');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:`linear-gradient(135deg,${B} 0%,${T} 100%)`, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', position:'relative' }}>

      {/* Back to main app */}
      <Link to="/" style={{ position:'absolute', top:20, left:20, display:'flex', alignItems:'center', gap:7, color:'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:13, background:'rgba(255,255,255,0.12)', padding:'8px 14px', borderRadius:8, backdropFilter:'blur(4px)' }}>
        <svg viewBox="0 0 24 24" style={{ width:15, height:15, fill:'none', stroke:'currentColor', strokeWidth:2 }}>
          <path d="M19 12H5M12 5l-7 7 7 7"/>
        </svg>
        Retour à LOMED MEDICARE
      </Link>

      <div style={{ background:'#fff', borderRadius:20, boxShadow:'0 25px 60px rgba(0,0,0,0.25)', width:'100%', maxWidth:440, overflow:'hidden' }}>

        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,${B},${T})`, padding:'2rem', textAlign:'center' }}>
          <div style={{ width:56, height:56, background:'rgba(255,255,255,0.15)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', backdropFilter:'blur(4px)' }}>
            <svg width="30" height="30" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="15" fill="none" stroke="white" strokeWidth="1.5" strokeDasharray="65 20"/>
              <rect x="14" y="17" width="12" height="6" rx="1.5" fill="white"/>
              <rect x="17" y="14" width="6" height="12" rx="1.5" fill="rgba(255,255,255,0.6)"/>
            </svg>
          </div>
          <div style={{ color:'#fff', fontSize:17, fontWeight:600, letterSpacing:'0.5px' }}>LOMED MEDICARE</div>
          <div style={{ color:'rgba(255,255,255,0.7)', fontSize:12, marginTop:4 }}>StockMed — Gestion de stock</div>
        </div>

        <div style={{ padding:'1.8rem' }}>
          {/* Tabs */}
          <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden', marginBottom:'1.5rem' }}>
            {[['login','Connexion'],['register','Créer un compte']].map(([m,l]) => (
              <button key={m} onClick={()=>setMode(m)} style={{ flex:1, background:mode===m?B:'#fff', color:mode===m?'#fff':'#64748b', border:'none', padding:'10px', fontSize:13, fontWeight:mode===m?600:400, cursor:'pointer', transition:'all 0.2s' }}>{l}</button>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {mode === 'register' && (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Prénom</label>
                    <input style={inp} value={form.firstName} onChange={e=>set('firstName',e.target.value)} placeholder="Ahmed"
                      onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Nom</label>
                    <input style={inp} value={form.lastName} onChange={e=>set('lastName',e.target.value)} placeholder="Benali"
                      onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Nom de l'établissement *</label>
                  <input style={inp} value={form.company} onChange={e=>set('company',e.target.value)} placeholder="Pharmacie Al Shifa" required
                    onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Type d'établissement</label>
                  <select style={inp} value={form.companyType} onChange={e=>set('companyType',e.target.value)}>
                    {[['pharmacie','💊 Pharmacie'],['clinique','🏥 Clinique'],['hopital','🏨 Hôpital'],['laboratoire','🔬 Laboratoire'],['autre','📋 Autre']].map(([v,l])=>(
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Email professionnel *</label>
                  <input type="email" style={{ ...inp, borderColor: form.email && !validateEmail(form.email)?'#dc2626':'#e2e8f0' }}
                    value={form.email} onChange={e=>set('email',e.target.value)} placeholder="contact@pharmacie.ma" required
                    onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor= form.email&&!validateEmail(form.email)?'#dc2626':'#e2e8f0'}/>
                  {form.email && !validateEmail(form.email) && <div style={{ fontSize:10, color:'#dc2626', marginTop:3 }}>Format email invalide</div>}
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Téléphone</label>
                  <input style={inp} value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+212 6XX XXX XXX"
                    onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
                </div>
              </>
            )}

            <div>
              <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>
                {mode==='login'?'Identifiant ou Email *':'Nom d\'utilisateur *'}
              </label>
              <input style={inp} value={form.username} onChange={e=>set('username',e.target.value)}
                placeholder={mode==='login'?'admin ou email@...':'nom_utilisateur'} required
                onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
            </div>

            <div>
              <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Mot de passe *</label>
              <input type="password" style={inp} value={form.password} onChange={e=>set('password',e.target.value)}
                placeholder="••••••••" required
                onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
              {mode==='register' && <PasswordStrength password={form.password}/>}
            </div>

            {mode === 'register' && (
              <>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Confirmer le mot de passe *</label>
                  <input type="password" style={{ ...inp, borderColor: form.confirmPassword&&form.password!==form.confirmPassword?'#dc2626':'#e2e8f0' }}
                    value={form.confirmPassword} onChange={e=>set('confirmPassword',e.target.value)} placeholder="••••••••" required
                    onFocus={e=>e.target.style.borderColor=B} onBlur={e=>e.target.style.borderColor= form.confirmPassword&&form.password!==form.confirmPassword?'#dc2626':'#e2e8f0'}/>
                  {form.confirmPassword && form.password !== form.confirmPassword && (
                    <div style={{ fontSize:10, color:'#dc2626', marginTop:3 }}>Les mots de passe ne correspondent pas</div>
                  )}
                </div>
                <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', fontSize:12, color:'#64748b', lineHeight:1.5 }}>
                  <input type="checkbox" checked={form.agreeTerms} onChange={e=>set('agreeTerms',e.target.checked)} style={{ marginTop:2, cursor:'pointer' }}/>
                  J'accepte les <span style={{ color:B, textDecoration:'underline' }}>conditions d'utilisation</span> et la politique de confidentialité de LOMED MEDICARE
                </label>
              </>
            )}

            <button type="submit" disabled={loading || (mode==='register' && (!form.agreeTerms || form.password!==form.confirmPassword))} style={{
              background: loading?'#94a3b8':`linear-gradient(135deg,${B},${T})`,
              color:'#fff', border:'none', padding:'12px', borderRadius:9, fontSize:14,
              fontWeight:600, cursor:'pointer', marginTop:4,
              opacity: (mode==='register' && (!form.agreeTerms || form.password!==form.confirmPassword)) ? 0.6 : 1,
              transition:'all 0.2s'
            }}>
              {loading ? '⏳ Chargement...' : mode==='login' ? 'Se connecter' : 'Créer mon compte gratuit →'}
            </button>
          </form>

          {mode === 'register' && (
            <div style={{ marginTop:14, background:'#E1F5EE', border:'1px solid #9FE1CB', borderRadius:10, padding:'10px 12px', fontSize:12, color:T }}>
              ✅ <strong>Plan Gratuit inclus :</strong> 20 produits, alertes stock, suivi des mouvements, tableau de bord
            </div>
          )}

          {mode === 'login' && (
            <div style={{ textAlign:'center', marginTop:14, fontSize:12, color:'#94a3b8' }}>
              Pas encore de compte ?{' '}
              <button onClick={()=>setMode('register')} style={{ color:B, background:'none', border:'none', cursor:'pointer', fontWeight:600, fontSize:12 }}>
                Créer un compte gratuit
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

